import * as Sentry from "@sentry/react";
import { createRouter, ErrorComponent } from "@tanstack/react-router";
import { DevErrorComponent } from "./components/dev-error-component";
import { isReloadPending, markReloadPending } from "./lib/stale-bundle";
import { routeTree } from "./routeTree.gen";

function isChunkLoadError(error: Error): boolean {
  const msg = error.message;
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    (error.name === "TypeError" && msg.includes("error loading dynamically imported module")) ||
    // Safari / iOS WebKit's wording for the same failure (Sentry BDAY-WEB-D, 2026-10-21) —
    // without it a stale Safari tab got the error page instead of the reload.
    (error.name === "TypeError" && msg.includes("Importing a module script failed")) ||
    (error.name === "TypeError" && msg === "Load failed")
  );
}

export const router = createRouter({
  routeTree,
  // Preload a link's split route chunk on hover/focus ("intent") so the
  // download races the user's click — with autoCodeSplitting active, this is
  // what keeps page-to-page navigation from waiting on the network.
  defaultPreload: "intent",
  defaultPreloadStaleTime: 30_000,
  defaultErrorComponent: import.meta.env.DEV ? DevErrorComponent : ErrorComponent,
  defaultOnCatch: (error) => {
    // The stale-bundle reload is already in flight (lib/stale-bundle.ts) — don't report
    // its fallout or reload a second time.
    if (isReloadPending()) return;
    Sentry.captureException(error);
    if (error instanceof Error && isChunkLoadError(error)) {
      markReloadPending();
      window.location.reload();
    }
  },
  scrollRestoration: true,
  scrollToTopSelectors: ["#main-content"],
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
